import { useEffect, useRef } from "react";

// Canvas 2D drawing can't consume CSS custom properties directly, so the brand pink RGB
// components are duplicated here. Keep in sync with --brand-pink in src/index.css (#E91E8C).
const BRAND_PINK_RGB = "233,30,140";

export const WaveCanvas = () => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf: number;
    let t = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const waves = Array.from({ length: 32 }, (_, i) => {
      const band = i / 32;
      const bright = i >= 10 && i <= 16;
      return {
        yBase: 0.45 + band * 0.65,
        amplitude: 28 + band * 55,
        freq: 0.0018 + band * 0.0012,
        speed: 0.008 + band * 0.004,
        phase: i * 1.37,
        opacity: bright ? 0.22 + Math.random() * 0.28 : 0.03 + Math.random() * 0.08,
        lw: bright ? 1.0 + Math.random() * 0.9 : 0.4 + Math.random() * 0.6,
      };
    });

    const tick = () => {
      const { width: W, height: H } = canvas;
      ctx.clearRect(0, 0, W, H);

      for (const w of waves) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${BRAND_PINK_RGB},${w.opacity})`;
        ctx.lineWidth = w.lw;
        ctx.shadowColor = `rgb(${BRAND_PINK_RGB})`;
        ctx.shadowBlur = w.lw > 1 ? 5 : 0;
        const yb = w.yBase * H;
        for (let x = 0; x <= W; x += 2) {
          const y =
            yb +
            Math.sin(x * w.freq + t * w.speed + w.phase) * w.amplitude +
            Math.sin(x * w.freq * 1.7 + t * w.speed * 0.6 + w.phase * 0.5) * (w.amplitude * 0.3);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Vignette
      const g = ctx.createRadialGradient(W * 0.35, H * 0.75, 0, W * 0.35, H * 0.75, W * 0.85);
      g.addColorStop(0, "rgba(30,34,53,0)");
      g.addColorStop(1, "rgba(30,34,53,0.5)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);

      t += 1;
      raf = requestAnimationFrame(tick);
    };

    tick();
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className="absolute inset-0 w-full h-full block pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};
